'use client';

/**
 * Hover-preview media primitives shared by every card variant that plays
 * a course's {@code previewVideoUrl} on hover (currently 'udemy' and
 * 'classicNetflixZoom'). Mounting is the caller's job — these components
 * assume "mounted === should be playing", matching the pattern where the
 * hover popover/zoom only renders this subtree while hovered.
 */
import { useEffect, useRef, useState } from 'react';

/**
 * True when the preview URL is an embeddable player page (YouTube,
 * Cinema8, Vimeo, or any {@code /embed/} route) rather than a direct
 * media file. Embeds must render in an {@code <iframe>}; direct files
 * (mp4/webm) render in a {@code <video>}.
 */
export function isEmbedUrl(url: string): boolean {
  return /youtube\.com\/embed|youtube-nocookie\.com|player\.vimeo\.com|cinema8\.com|\/embed\//i.test(
    url,
  );
}

/**
 * Muted, autoplaying preview for embed-hosted clips (YouTube / Cinema8).
 * The caller mounts this only while hovered, so the iframe exists only
 * during hover and playback stops when it unmounts. Player chrome is
 * suppressed via the embed params baked into {@code src} by the host
 * adapter ({@code controls=0}, {@code modestbranding}, etc.). The player
 * stays interactive (pointer events on) so viewers can use its controls.
 *
 * <p>An embed can't paint its first frame instantly, so the poster is held
 * on top and crossfaded out on the iframe's {@code load} event — the viewer
 * sees the cover fade smoothly into the playing clip instead of a blank
 * frame or an abrupt swap. The poster never takes pointer events, so it
 * can't block the player's controls even before it fades.
 */
export function PreviewEmbed({
  src,
  title,
  poster,
}: {
  src: string;
  title: string;
  poster?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      <iframe
        src={src}
        title={title}
        allow="autoplay; encrypted-media; picture-in-picture"
        loading="lazy"
        onLoad={() => setLoaded(true)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
      {poster && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `center / cover no-repeat url("${poster}")`,
            opacity: loaded ? 0 : 1,
            transition: 'opacity 240ms ease',
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
}

/**
 * Muted, looping preview clip. The caller mounts this only while
 * hovered, so mounting === "should be playing"; we kick off
 * {@code play()} on mount and swallow the promise rejection browsers
 * throw when autoplay is blocked (the poster stays visible).
 */
export function PreviewVideo({ src, poster }: { src: string; poster?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        /* autoplay blocked — poster fallback stays visible */
      });
    }
  }, [src]);
  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={poster}
      // Browsers (notably Chrome/Android) render their own "tap to unmute"
      // affordance over a muted, controls-less <video>. That native tap
      // fires as an ordinary click on this element, which — since every
      // caller mounts PreviewVideo inside a PortalLink — would otherwise
      // bubble up and navigate to the course instead of just unmuting.
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
    />
  );
}
